import { Transition } from "@headlessui/react";
import type { Toast } from "react-hot-toast";
import toast from "react-hot-toast";
import { MdCheck } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import { AiOutlineExclamation } from "react-icons/ai";

interface ToastNotificationProps {
  message: string;
  subMessage?: string;
  isError?: boolean;
  /**
   * The toast instance from react-hot-toast's `toast.custom((t) => ...)` render
   * callback. Passing it lets the toast drive its enter/leave animation off the
   * library's real lifecycle (`t.visible`) and lets the close button actually
   * dismiss the toast (`toast.dismiss(t.id)`) instead of only hiding it locally.
   */
  t?: Toast;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ message, subMessage, isError, t }) => {
  // Drive visibility off react-hot-toast's lifecycle when available so the
  // library's auto-dismiss/leave animation stays in sync. Fall back to always
  // visible for any legacy usage that renders this component directly.
  const show = t ? t.visible : true;

  const handleClose = () => {
    if (t) {
      toast.dismiss(t.id);
    }
  };

  return (
    <Transition
      show={show}
      appear
      enter="transform transition duration-300 ease-out"
      enterFrom="translate-x-6 opacity-0"
      enterTo="translate-x-0 opacity-100"
      leave="transform transition duration-200 ease-in"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo="translate-x-6 opacity-0"
    >
      <div
        role="status"
        aria-live={isError ? "assertive" : "polite"}
        className={`pointer-events-auto flex w-[320px] items-start gap-3 overflow-hidden rounded-xl border p-4 shadow-lg 2xl:w-[400px] ${
          isError ? "border-red-200 bg-[#FFF1F1]" : "border-[#A0BCE4]/40 bg-[#193A6A]"
        }`}
      >
        <div
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            isError ? "bg-[#FF0303]" : "bg-green-600"
          }`}
        >
          {isError ? (
            <AiOutlineExclamation className="h-4 w-4 text-white" />
          ) : (
            <MdCheck className="h-4 w-4 text-white" />
          )}
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          <p
            className={`break-words text-xs font-semibold capitalize leading-4 2xl:text-sm ${
              isError ? "text-[#FF0303]" : "text-white"
            }`}
          >
            {message}
          </p>
          {subMessage ? (
            <p className={`mt-1 break-words text-[11px] leading-4 ${isError ? "text-red-500/80" : "text-[#A0BCE4]"}`}>
              {subMessage}
            </p>
          ) : null}
        </div>

        <button
          type="button"
          onClick={handleClose}
          aria-label="Dismiss notification"
          className="shrink-0 rounded-full p-0.5 transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current"
        >
          <RxCross2 aria-hidden="true" className={`h-5 w-5 ${isError ? "text-[#FF0303]" : "text-[#A0BCE4]"}`} />
        </button>
      </div>
    </Transition>
  );
};

export default ToastNotification;
